/**
 * Full automation of a promoted workflow: which of its tool steps run without a supervisor approving
 * them. Off for every step by default, and promotion never changes it. Turning a step on asks first,
 * because from then on that step acts on real tickets unattended; the account's automation policy can
 * still hold it back, which the confirmation says.
 */

import { useState } from 'react';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useBlueprintAutomation, useSetAutomation } from '@/hooks/use-promoted-blueprints';
import { getApiErrorMessage } from '@/lib/utils';
import { isWriteStep, nextPreApproved } from '../automation';
import type { BlueprintToolStep } from '../types';

interface AutomationSectionProps {
  blueprintId: string;
  canEdit: boolean;
}

export const AutomationSection = ({ blueprintId, canEdit }: AutomationSectionProps) => {
  const { data: automation, isLoading } = useBlueprintAutomation(blueprintId);
  const setAutomation = useSetAutomation();
  const [confirming, setConfirming] = useState<BlueprintToolStep | null>(null);

  if (isLoading || !automation) return <Skeleton className='mt-4 h-16 w-full rounded-lg' />;
  if (!automation.steps.length) return null;

  const save = async (stepId: string, on: boolean) => {
    try {
      await setAutomation.mutateAsync({ id: blueprintId, stepIds: nextPreApproved(automation.steps, stepId, on) });
      toast.success(on ? 'This step now runs without approval' : 'This step needs approval again');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not change this step'));
    }
  };

  const toggle = (step: BlueprintToolStep, on: boolean) => {
    // Turning approval off is the risky direction; turning it back on needs no second thought.
    if (on) setConfirming(step);
    else void save(step.stepId, false);
  };

  return (
    <section className='mt-4 rounded-lg border' data-testid='automation-section'>
      <div className='border-b bg-muted/40 px-3 py-2'>
        <h3 className='text-xs font-semibold text-foreground'>Full automation</h3>
        <p className='text-[11px] leading-relaxed text-muted-foreground'>
          Steps that change something wait for a supervisor to approve them. Switch a step on to let it run on its own.
          Auto-send only decides the reply.
        </p>
      </div>
      <ul className='divide-y'>
        {automation.steps.map((step) => (
          <li key={step.stepId} className='flex items-center justify-between gap-3 px-3 py-2' data-testid={`step-${step.stepId}`}>
            <div className='flex min-w-0 flex-col gap-0.5'>
              <span className='flex items-center gap-1.5'>
                <code className='truncate text-[12px]'>{step.toolName}</code>
                {isWriteStep(step) ? (
                  <Badge variant='warning' className='text-[10px]'>
                    Changes data
                  </Badge>
                ) : (
                  <Badge variant='secondary' className='text-[10px]'>
                    Read
                  </Badge>
                )}
              </span>
              {step.description && <span className='truncate text-[11px] text-muted-foreground'>{step.description}</span>}
            </div>
            <span className='flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground'>
              {step.preApproved ? (
                <ShieldAlert className='size-3.5 text-amber-600' aria-hidden />
              ) : (
                <ShieldCheck className='size-3.5' aria-hidden />
              )}
              {step.preApproved ? 'Automatic' : 'Needs approval'}
              <Switch
                aria-label={`Run ${step.toolName} without approval`}
                checked={step.preApproved}
                disabled={!canEdit || setAutomation.isPending}
                onCheckedChange={(on) => toggle(step, on)}
              />
            </span>
          </li>
        ))}
      </ul>
      {automation.preApprovedAt && (
        <p className='border-t px-3 py-1.5 text-[11px] text-muted-foreground'>
          Last changed {new Date(automation.preApprovedAt).toLocaleString()}
        </p>
      )}

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run {confirming?.toolName} without approval?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming && isWriteStep(confirming)
                ? 'This step changes data. From now on it runs on customers’ tickets without a supervisor approving it first.'
                : 'This step will run without a supervisor approving it first.'}{' '}
              The account’s automation policy for this intent can still hold it for approval.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep approval</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming) void save(confirming.stepId, true);
                setConfirming(null);
              }}
            >
              Run without approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
